import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ExportFormat, useExportBoard } from '@/entities/board';
import { FormError } from '@/shared/forms';
import { Button, Dialog, HelpRow } from '@/shared/ui';

import styles from './Portability.module.css';

const FORMATS: readonly ExportFormat[] = ['zip', 'json', 'csv'];

/** Download a board as a complete ZIP, structured JSON or a flat CSV. Changes nothing on the board. */
export function ExportBoardDialog({ boardId, onClose }: { boardId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const exportBoard = useExportBoard(boardId);
  const [format, setFormat] = useState<ExportFormat>('zip');

  return (
    <Dialog title={t('portability.export.title')} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (!exportBoard.isPending) exportBoard.mutate(format);
        }}
      >
        <HelpRow
          topic={t('portability.export.title')}
          topicId="import-export"
          text={t('help.export')}
        >
          <p className={styles.intro}>{t('portability.export.intro')}</p>
        </HelpRow>
        <fieldset className={styles.formats}>
          <legend className="visually-hidden">{t('portability.export.title')}</legend>
          {FORMATS.map((value) => (
            <label key={value} className={styles.format}>
              <input
                type="radio"
                name="export-format"
                value={value}
                checked={format === value}
                onChange={() => {
                  setFormat(value);
                  exportBoard.reset();
                }}
              />
              <span>
                <span className={styles.formatTitle}>
                  {t(`portability.export.format.${value}.title`)}
                </span>
                <span className={styles.formatDesc}>
                  {t(`portability.export.format.${value}.desc`)}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        <p className={styles.note}>{t('portability.export.excluded')}</p>
        {format === 'csv' && <p className={styles.note}>{t('portability.export.csvNote')}</p>}
        <FormError error={exportBoard.error} />
        {exportBoard.isSuccess && (
          <p className={styles.note} role="status">
            {t('portability.export.done')}
          </p>
        )}
        {exportBoard.isPending && (
          <p className={styles.note} role="status">
            {t('portability.export.working')}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={exportBoard.isPending}>
            {t('portability.export.action')}
          </Button>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
